import { useEffect, useState } from "react";
import { isConnectEnabled } from "../utils/connectVisibility";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { BarChart3, Cable, Calendar, ClipboardList, DollarSign, Globe2, Headphones, LayoutDashboard, Mail, RadioTower, Settings, Store, Trophy, Users } from "lucide-react";
import { API } from "../App";
import { adminOrgRoute } from "../config/adminPortalRoutes";


const canAccess = (user, permission) => !permission || user?.role !== "org_admin"
  || !Array.isArray(user?.admin_permissions) || user.admin_permissions.includes(permission);

// One subscription per shell, shared by the desktop and mobile navigation.
export function useOrgAdminNavigation({ platform, organization, token, user }) {
  const [data, setData] = useState(null);
  const orgId = organization?.org_id;
  const paymentsAllowed = canAccess(user, "payments");
  const modulesAllowed = canAccess(user, "teams") || canAccess(user, "settings");
  useEffect(() => {
    if (platform || !orgId || !token || user?.role !== "org_admin") return undefined;
    let active = true;
    let request = 0;
    const headers = { Authorization: `Bearer ${token}` };
    const refresh = async () => {
      const currentRequest = ++request;
      const [apps, financials] = await Promise.all([
        modulesAllowed ? axios.get(`${API}/marketplace/navigation`, { headers }).catch(() => null) : null,
        paymentsAllowed ? axios.get(`${API}/admin/financial-stats`, { headers }).catch(() => null) : null,
      ]);
      if (active && currentRequest === request) setData({
        orgId, token,
        installed: new Set((apps?.data?.apps || []).filter((app) => app.installed === true).map((app) => app.slug)),
        pendingCount: Math.max(0, Number(financials?.data?.pending_count) || 0),
      });
    };
    refresh();
    window.addEventListener("marketplace-installations-changed", refresh);
    return () => { active = false; window.removeEventListener("marketplace-installations-changed", refresh); };
  }, [orgId, modulesAllowed, paymentsAllowed, platform, token, user?.role]);
  const current = !platform && data?.orgId === orgId && data?.token === token ? data : null;
  return { installed: current?.installed || new Set(), pendingCount: paymentsAllowed ? current?.pendingCount || 0 : 0 };
}

const sectionsFor = (installed, pendingCount) => [
  { items: [{ label: "Today", icon: LayoutDashboard, destination: "today" }] },
  { label: "Programs", items: [
    { label: "Programs", icon: ClipboardList, destination: "programs", permission: "programs" },
    ...(installed.has("leagues") ? [{ label: "Leagues", icon: Trophy, destination: "extensions/leagues", permission: "teams" }] : []),
    ...(installed.has("tournaments") ? [{ label: "Tournaments", icon: Trophy, destination: "extensions/tournaments", permission: "teams" }] : []),
  ] },
  { label: "Members", items: [{ label: "People & Families", icon: Users, destination: "people", permission: "registrations" }] },
  { label: "Operations", items: [
    { label: "Scheduling", icon: Calendar, destination: "operations/scheduling", match: "operations", permission: "teams" },
    { label: "Communication", icon: Mail, destination: "communications", permission: "teams" },
    { label: "Support", icon: Headphones, destination: "support", permission: "teams" },
  ] },
  { label: "Finance", items: [{ label: "Financials", icon: DollarSign, destination: "finance", permission: "payments", badge: pendingCount }] },
  ...(isConnectEnabled() ? [{ items: [{ label: "Connect", icon: Cable, destination: "connect", permission: "reports" }] }] : []),
  { label: "Insights", items: [{ label: "Reports", icon: BarChart3, destination: "finance/reports", permission: "reports" }] },
  { label: "Extend", items: [
    ...(installed.has("wstl") ? [{ label: "WSTL Officiating", icon: RadioTower, destination: "extensions/wstl", permission: "teams" }] : []),
    ...(installed.has("website_pro") ? [{ label: "Website", icon: Globe2, destination: "website", permission: "settings" }] : []),
    { label: "Marketplace", icon: Store, destination: "marketplace", permission: "settings" },
  ] },
];

function OrgNavigationLink({ item, orgSlug, close }) {
  const {workspaceOrgId} = useParams();
  const { pathname } = useLocation();
  const destination = adminOrgRoute(orgSlug, item.destination, workspaceOrgId);
  const match = item.match ? adminOrgRoute(orgSlug, item.match, workspaceOrgId) : destination;
  const active = (pathname === match || pathname.startsWith(`${match}/`))
    && !(item.destination === "finance" && pathname.startsWith(`${adminOrgRoute(orgSlug, "finance/reports", workspaceOrgId)}`));
  const Icon = item.icon;
  return <Link
    aria-current={active ? "page" : undefined}
    className={`admin-nav-item flex items-center justify-between rounded-xl px-4 py-2.5 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white ${active ? "active bg-white/[0.12] text-white shadow-sm" : "text-white/60 hover:bg-white/[0.07] hover:text-white"}`}
    onClick={close} to={destination}
  >
    <span className="flex items-center gap-3"><Icon aria-hidden="true" className="h-5 w-5 shrink-0" /><span className="text-sm font-medium">{item.label}</span></span>
    {item.badge > 0 && <span aria-label={`${item.badge} pending payments`} className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold ${active ? "bg-white text-slate-900" : "bg-[#087c71] text-white"}`}>{item.badge > 99 ? "99+" : item.badge}</span>}
  </Link>;
}

export function OrgAdminSettings({ user, orgSlug, close }) {
  return canAccess(user, "settings") ? <div className="px-3 pt-3"><OrgNavigationLink close={close} orgSlug={orgSlug} item={{ label: "Settings", icon: Settings, destination: "settings" }} /></div> : null;
}

export default function OrgAdminNavigation({ user, installed, pendingCount, orgSlug, close }) {
  const sections = sectionsFor(installed, pendingCount).map((section) => ({ ...section, items: section.items.filter((item) => canAccess(user, item.permission)) })).filter((section) => section.items.length);
  return sections.map((section, index) => <div className={index > 0 ? "mt-4" : ""} key={section.label || section.items[0].label}>
    {section.label && <p className="px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/60">{section.label}</p>}
    <div className="space-y-0.5">{section.items.map((item) => <OrgNavigationLink close={close} item={item} key={item.label} orgSlug={orgSlug} />)}</div>
  </div>);
}
